import { useDeleteAgentConversationMutation } from '@saaga/shared/store/api.store'
import { setDraftConversationId } from '@saaga/shared/store/slices/draft-agency-chat.slice'
import { useAppDispatch, useAppSelector } from '@saaga/shared/store/store'
import { useCallback } from 'react'

import type { ChatInputMode } from '@/components/chat/chat-input-mode.type'
import type { UseChatMessagesReturn } from '@/routes/chat/use-chat-messages.hook'

interface UseDraftAgencyChatActionsProps {
  inputValue: string
  inputMode: ChatInputMode
  agencySlug: string | undefined
  messages: UseChatMessagesReturn
  onInputClear: () => void
}

interface DraftAgencyChatActions {
  handleSend: () => void
  handleReset: () => void
}

export const useDraftAgencyChatActions = ({
  inputValue,
  inputMode,
  agencySlug,
  messages,
  onInputClear,
}: UseDraftAgencyChatActionsProps): DraftAgencyChatActions => {
  const dispatch = useAppDispatch()
  const draftConversationId = useAppSelector((s) => s.draftAgencyChat.draftConversationId)
  const [deleteConversation] = useDeleteAgentConversationMutation()

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim()
    if (!trimmed || !agencySlug) return
    if (inputMode === 'note') {
      messages.sendNote(trimmed)
      onInputClear()
      return
    }
    messages.sendMessage(trimmed)
    onInputClear()
  }, [inputValue, inputMode, agencySlug, messages, onInputClear])

  const handleReset = useCallback(async () => {
    if (draftConversationId) {
      try {
        await deleteConversation(draftConversationId).unwrap()
      } catch {}
    }
    dispatch(setDraftConversationId(null))
  }, [draftConversationId, deleteConversation, dispatch])

  return { handleSend, handleReset }
}
